<script setup lang="ts">
/**
 * ลงออเดอร์ › 2 แพ็กและเมนู › "ขยาย" (owner 28 ก.ย. 69): every dish on sale with its photo, in half the screen — the chat
 * stays readable in the other half (InboxView hides the chat list meanwhile). Same form as the small picker
 * (MenuPicker.vue): search, type chips, "เฉพาะที่เลือก", −/+/type, a sticky count against the pack. "ย่อกลับ" returns
 * to the side panel with the same state. Photos: catalog `img` (POS filestore), lazy, whole dish shown (never cropped).
 * Round 2: "สุ่มเมนูที่เหลือ N กล่อง" next to the search (POS #btn-random rules, lib/menuRead.ts randomFill) — a type
 * chip picked below limits it ("สุ่มไก่ที่เหลือ…"); the picks are ordinary quantities the staff change as usual.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PosMenu } from '../../../api/pos/types'
import { baht } from '../../../lib/inbox'
import { counts, qtyOf } from '../../../lib/order'
import { dishName, kindLabel, kindOf, KIND_ORDER, pCodeOf, randomFill, randomPool } from '../../../lib/menuRead'
import { useInbox } from '../../../stores/inbox'
import { useMenuRead } from '../../../stores/menuRead'
import { useOrders } from '../../../stores/order'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const orders = useOrders()
const mr = useMenuRead()

const f = computed(() => orders.forms[props.convId] ?? null)
const cat = computed(() => orders.catalog)
const pack = computed(() => cat.value?.set_menus.find((s) => s.id === f.value?.setMenuId) ?? null)
const contPack = computed(() => {
  const cid = f.value?.customerId
  const packs = cid ? (orders.histories[cid]?.data?.open_packs ?? []) : []
  return packs.find((p) => p.order_header_id === f.value?.continueHeaderId) ?? null
})
const quota = computed(() => (contPack.value ? contPack.value.meals_remaining : (pack.value?.meals ?? 0)))
const packName = computed(() => (contPack.value ? `สั่งต่อ ${contPack.value.set_menu_name}` : (pack.value?.name ?? 'ยังไม่ได้เลือกแพ็ก')))
const cnt = computed(() => (f.value ? counts(f.value, cat.value) : { meals: 0, snacks: 0 }))
const picked = computed(() => f.value?.items.length ?? 0)


const term = ref('')
const kind = ref<string | null>(null)
const onlyPicked = computed(() => !!mr.onlyPicked[props.convId]) // shared with the small list (MenuPicker.vue)
const all = computed(() => cat.value?.menus ?? [])
const kinds = computed(() => {
  const n = new Map<string, number>()
  for (const m of all.value) n.set(kindOf(m), (n.get(kindOf(m)) ?? 0) + 1)
  return KIND_ORDER.filter((k) => n.has(k)).map((k) => ({ k, label: kindLabel(k), n: n.get(k) ?? 0 }))
})
const shown = computed<PosMenu[]>(() => {
  const t = term.value.trim().toLowerCase().replace(/\s+/g, '')
  return all.value.filter((m) => {
    if (onlyPicked.value && !qty(m.id)) return false
    if (kind.value && kindOf(m) !== kind.value) return false
    if (!t) return true
    return m.name.toLowerCase().replace(/\s+/g, '').includes(t) || (m.code ?? '').toLowerCase().includes(t)
  })
})

function qty(id: number): number {
  return f.value ? qtyOf(f.value, id) : 0
}
function set(id: number, n: number): void {
  orders.setQty(props.convId, id, n)
}
function typed(id: number, e: Event): void {
  const el = e.target as HTMLInputElement
  set(id, Number(el.value.replace(/\D/g, '') || '0'))
  el.value = String(qty(id))
}
function sub(m: PosMenu): string | null {
  if (m.type === 'snacks') return `${baht(m.price)} · ไม่นับในแพ็ก`
  if (m.plus > 0) return `+${baht(m.plus)} ต่อกล่อง`
  return null
}
const broken = ref<Set<number>>(new Set())
function markBroken(id: number): void {
  broken.value = new Set(broken.value).add(id)
}

const status = computed(() => {
  if (!quota.value) return null
  const d = quota.value - cnt.value.meals
  if (d === 0) return { tone: 'ok', text: 'ครบแพ็กพอดี' }
  if (d > 0) return { tone: '', text: `ขาดอีก ${d} มื้อ` }
  return { tone: 'warn', text: `เกินแพ็ก ${-d} กล่อง คิดราคาเมนู` }
})

// ------------------------------------------------ "สุ่มเมนูที่เหลือ N กล่อง"
const inbox = useInbox()
const missing = computed(() => (quota.value ? Math.max(0, quota.value - cnt.value.meals) : 0))
/** Chip "รวมเมนูบวกเงิน" (off by default, like the POS button) — owner 28 ก.ย. 69 */
const withPlus = ref(false)
const rndPool = computed(() => {
  const pool = randomPool(all.value, contPack.value?.set_menu_name ?? pack.value?.name ?? null, withPlus.value)
  return kind.value ? pool.filter((m) => kindOf(m) === kind.value) : pool
})
const rndLabel = computed(() => `สุ่ม${kind.value && kind.value !== 'other' ? kindLabel(kind.value) : 'เมนู'}ที่เหลือ ${missing.value} กล่อง`)
function fillRandom(): void {
  const items = f.value?.items ?? []
  const adds = randomFill(items.map((i) => ({ menuId: i.menu_id, qty: i.qty })), rndPool.value, missing.value, null)
  for (const a of adds) set(a.menuId, qty(a.menuId) + a.qty)
  const n = adds.reduce((x, a) => x + a.qty, 0)
  if (n) inbox.flash(`สุ่มเพิ่ม ${n} กล่อง จาก ${adds.length} เมนู · แก้จำนวนได้ตามปกติ`)
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) mr.collapse()
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <section class="mg" aria-label="เลือกเมนูแบบขยาย">
    <header class="mg-h">
      <div class="mg-t">
        <h2>เลือกเมนู</h2>
        <span class="mg-p">{{ packName }}</span>
      </div>
      <span class="grow"></span>
      <span v-if="quota" class="cnt" :class="{ warn: status?.tone === 'warn', ok: status?.tone === 'ok' }">
        <b class="mono">{{ cnt.meals }}/{{ quota }}</b> มื้อ<template v-if="status"> · {{ status.text }}</template>
      </span>
      <span v-else class="cnt"><b class="mono">{{ cnt.meals }}</b> มื้อ</span>
      <span v-if="cnt.snacks" class="cnt mut">ของว่าง {{ cnt.snacks }}</span>
      <button type="button" class="mg-x" title="ย่อกลับไปแผงด้านข้าง (Esc)" @click="mr.collapse()"><Icon name="minimize" />ย่อกลับ</button>
    </header>

    <div class="mg-tools">
      <div class="mg-r">
        <label class="mg-s"
          ><Icon name="search" /><input v-model="term" type="search" aria-label="ค้นหาเมนู" placeholder="ค้นหาชื่อหรือรหัส P" autocomplete="off"
        /></label>
        <button
          v-if="missing > 0"
          type="button"
          class="mg-rb"
          :disabled="!rndPool.length"
          :title="
            rndPool.length
              ? `สุ่มจากเมนูที่ยังไม่ได้เลือกก่อน กระจายเท่าๆ กัน ไม่รวมของว่าง${withPlus ? '' : ' และเมนูบวกเงิน'} · เลือกประเภทด้านล่างเพื่อสุ่มเฉพาะประเภทนั้น`
              : 'ไม่มีเมนูที่สุ่มได้ในประเภทนี้'
          "
          @click="fillRandom"
        >
          <Icon name="shuffle" />{{ rndLabel }}
        </button>
      </div>
      <div class="chips" role="group" aria-label="ประเภทเมนู">
        <button type="button" class="chip" :class="{ on: kind === null }" :aria-pressed="kind === null" @click="kind = null">
          ทั้งหมด <span class="n">{{ all.length }}</span>
        </button>
        <button
          v-for="k in kinds"
          :key="k.k"
          type="button"
          class="chip"
          :class="{ on: kind === k.k }"
          :aria-pressed="kind === k.k"
          @click="kind = kind === k.k ? null : k.k"
        >
          {{ k.label }} <span class="n">{{ k.n }}</span>
        </button>
        <button type="button" class="chip" :class="{ on: withPlus }" :aria-pressed="withPlus" title="ให้ปุ่มสุ่มรวมเมนูบวกเงินด้วย (ลูกค้ายอมจ่ายเพิ่ม)" @click="withPlus = !withPlus">
          สุ่มรวมเมนูบวกเงิน
        </button>
        <span class="grow"></span>
        <button type="button" class="chip pick" :class="{ on: onlyPicked }" :aria-pressed="onlyPicked" @click="mr.toggleOnlyPicked(convId)">
          <Icon name="check" />เฉพาะที่เลือก <span class="n">{{ picked }}</span>
        </button>
      </div>
    </div>

    <div class="mg-b scroll">
      <ul v-if="shown.length" class="grid" aria-label="เมนูทั้งหมด">
        <li v-for="m in shown" :key="m.id" class="card" :class="{ on: qty(m.id) > 0 }">
          <button type="button" class="ph" :aria-label="`เพิ่ม ${m.name} 1 กล่อง`" @click="set(m.id, qty(m.id) + 1)">
            <img v-if="m.img && !broken.has(m.id)" :src="m.img" :alt="m.name" loading="lazy" decoding="async" @error="markBroken(m.id)" />
            <span v-else class="ph-none"><Icon name="utensils" /></span>
            <span v-if="pCodeOf(m.name)" class="code mono">{{ pCodeOf(m.name) }}</span>
            <span v-if="qty(m.id)" class="qb mono">×{{ qty(m.id) }}</span>
          </button>
          <div class="cb">
            <span class="nm">{{ dishName(m.name) }}</span>
            <span class="meta">
              <span class="kc">{{ kindLabel(kindOf(m)) }}</span>
              <span v-if="sub(m)" class="sub">{{ sub(m) }}</span>
            </span>
          </div>
          <span class="stp">
            <button type="button" class="stp-b" :disabled="qty(m.id) === 0" :aria-label="`ลด ${m.name}`" @click="set(m.id, qty(m.id) - 1)">
              <Icon name="minus" />
            </button>
            <input
              class="stp-v"
              type="text"
              inputmode="numeric"
              :value="qty(m.id)"
              :aria-label="`จำนวน ${m.name}`"
              @change="typed(m.id, $event)"
              @focus="($event.target as HTMLInputElement).select()"
            />
            <button type="button" class="stp-b" :aria-label="`เพิ่ม ${m.name}`" @click="set(m.id, qty(m.id) + 1)"><Icon name="plus" /></button>
          </span>
        </li>
      </ul>
      <p v-else class="empty">{{ onlyPicked ? 'ยังไม่ได้เลือกเมนู' : 'ไม่พบเมนูนี้' }}</p>
    </div>
  </section>
</template>

<style scoped>
.mg {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.grow {
  flex: 1;
}
.mono {
  font-family: var(--mono);
}
.mg-h {
  height: 68px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 0 20px;
  border-bottom: 1px solid var(--line);
}
.mg-t {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.mg-t h2 {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}
.mg-p {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  overflow-wrap: anywhere;
}
.cnt {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 11px;
  border-radius: 999px;
  background: #eef2f0;
  color: #3e4d46;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}
.cnt b {
  font-weight: 700;
}
.cnt.ok {
  background: var(--acs);
  color: var(--ac2);
}
.cnt.warn {
  background: var(--warns);
  color: var(--warn);
}
.cnt.mut {
  background: transparent;
  color: var(--mut);
  padding: 0 4px;
}
.mg-x {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}
.mg-x:hover {
  background: #f3f6f4;
}
.mg-x :deep(.ic) {
  width: 16px;
  height: 16px;
}
.mg-tools {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line2);
}
.mg-r {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mg-r .mg-s {
  flex: 1 1 auto;
  min-width: 0;
}
.mg-rb {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px 0 10px;
  border: 1px solid #9fcdb8;
  border-radius: 10px;
  background: #fff;
  color: var(--ac2);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.mg-rb:hover:not(:disabled) {
  background: var(--acs);
}
.mg-rb:disabled {
  border-color: var(--line);
  color: #8a9892;
  cursor: default;
}
.mg-rb :deep(.ic) {
  width: 15px;
  height: 15px;
}
.mg-s {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: var(--mut);
}
.mg-s:focus-within {
  border-color: var(--ac);
}
.mg-s :deep(.ic) {
  width: 16px;
  height: 16px;
  flex: none;
}
.mg-s input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  line-height: 20px;
  color: var(--ink);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 11px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  line-height: 20px;
  color: #3e4d46;
  white-space: nowrap;
}
.chip:hover {
  background: #f3f6f4;
}
.chip.on {
  border-color: #9fcdb8;
  background: var(--acs);
  color: var(--ac2);
  font-weight: 600;
}
.chip .n {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--mut);
}
.chip.on .n {
  color: var(--ac2);
}
.chip :deep(.ic) {
  width: 14px;
  height: 14px;
}
.mg-b {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 20px 20px;
  background: var(--bg);
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(176px, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}
.card.on {
  border-color: var(--ac);
  box-shadow: 0 0 0 1px var(--ac);
}
.ph {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #f1f4f2;
}
.ph img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.ph-none {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b5c2bc;
}
.ph-none :deep(.ic) {
  width: 28px;
  height: 28px;
}
.code {
  position: absolute;
  left: 8px;
  top: 8px;
  padding: 1px 7px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}
.qb {
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--ac);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}
.cb {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 9px 11px 6px;
}
.nm {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 20px;
  overflow-wrap: anywhere;
}
.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}
.kc {
  padding: 0 7px;
  border-radius: 6px;
  background: #eef2f0;
  font-size: 12px;
  line-height: 18px;
  color: #3e4d46;
}
.sub {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.stp {
  display: flex;
  align-items: center;
  margin: 4px 11px 11px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.card.on .stp {
  border-color: #9fcdb8;
}
.stp-b {
  width: 38px;
  height: 32px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ac2);
}
.stp-b:hover:not(:disabled) {
  background: var(--acs);
}
.stp-b:disabled {
  color: #b5c2bc;
}
.stp-b :deep(.ic) {
  width: 15px;
  height: 15px;
  stroke-width: 2.2;
}
.stp-v {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: 0;
  border-left: 1px solid var(--line2);
  border-right: 1px solid var(--line2);
  text-align: center;
  font: 600 13.5px/20px var(--mono);
  color: var(--ink);
  outline: none;
  background: transparent;
}
.stp-v:focus {
  background: #f5faf7;
}
.empty {
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
</style>
